import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import type { Project, ScheduleEntry, AbsenceEntry, ScheduleWorker } from '../types';

// ─── Helpers ────────────────────────────────

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(d: Date, n: number): Date {
  const date = new Date(d);
  date.setDate(date.getDate() + n);
  return date;
}

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

function formatDateRangeDE(from: Date, to: Date): string {
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'long', year: 'numeric' };
  return `${from.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} – ${to.toLocaleDateString('de-DE', opts)}`;
}

function getKW(d: Date): number {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const scheduleTypeLabels: Record<string, string> = {
  WORK: 'Arbeit',
  TRAVEL: 'Fahrt',
  ABSENCE: 'Abwesend',
};

const absenceTypeLabels: Record<string, string> = {
  VACATION: 'Urlaub',
  SICK: 'Krank',
  OTHER: 'Sonstiges',
};
const absenceTypeColors: Record<string, string> = {
  VACATION: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  SICK: 'bg-red-50 text-red-700 border-red-200',
  OTHER: 'bg-slate-50 text-slate-600 border-slate-200',
};

// Distinct colors for projects in the board
const projectColors = [
  'bg-brand-100 text-brand-800 border-brand-300',
  'bg-emerald-100 text-emerald-800 border-emerald-300',
  'bg-amber-100 text-amber-800 border-amber-300',
  'bg-purple-100 text-purple-800 border-purple-300',
  'bg-rose-100 text-rose-800 border-rose-300',
  'bg-cyan-100 text-cyan-800 border-cyan-300',
  'bg-orange-100 text-orange-800 border-orange-300',
  'bg-pink-100 text-pink-800 border-pink-300',
];

export default function SchedulePage() {
  const qc = useQueryClient();
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [showDays, setShowDays] = useState(5); // 5 = Mo-Fr, 7 = Mo-So
  const [assignModal, setAssignModal] = useState<{ userId: string; date: string } | null>(null);
  const [assignProject, setAssignProject] = useState('');
  const [assignType, setAssignType] = useState<'WORK' | 'TRAVEL'>('WORK');
  const [assignNote, setAssignNote] = useState('');
  const [absenceModal, setAbsenceModal] = useState(false);
  const [absenceForm, setAbsenceForm] = useState({
    userId: '',
    type: 'VACATION' as 'VACATION' | 'SICK' | 'OTHER',
    startDate: '',
    endDate: '',
    note: '',
  });
  const [activeTab, setActiveTab] = useState<'board' | 'absences'>('board');

  const weekEnd = addDays(weekStart, showDays - 1);
  const from = formatDate(weekStart);
  const to = formatDate(weekEnd);

  // Days array for the week
  const days = useMemo(() => {
    const result: Date[] = [];
    for (let i = 0; i < showDays; i++) {
      result.push(addDays(weekStart, i));
    }
    return result;
  }, [weekStart, showDays]);

  // ─── Queries ──────────────────────────────

  const { data: workers = [] } = useQuery<ScheduleWorker[]>({
    queryKey: ['schedule-workers'],
    queryFn: () => api.get('/schedule/workers').then((r) => r.data),
  });

  const { data: entries = [], isLoading: loadingEntries } = useQuery<ScheduleEntry[]>({
    queryKey: ['schedule-entries', from, to],
    queryFn: () => api.get(`/schedule/entries?from=${from}&to=${to}`).then((r) => r.data),
  });

  const { data: absences = [] } = useQuery<AbsenceEntry[]>({
    queryKey: ['schedule-absences', from, to],
    queryFn: () => api.get(`/schedule/absences?from=${from}&to=${to}`).then((r) => r.data),
  });

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects').then((r) => r.data),
  });

  // Build project color map
  const projectColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    projects.forEach((p, i) => {
      map[p.id] = projectColors[i % projectColors.length];
    });
    return map;
  }, [projects]);

  // Build lookup: entries by `userId-date`
  const entryLookup = useMemo(() => {
    const map: Record<string, ScheduleEntry[]> = {};
    entries.forEach((e) => {
      const dateStr = e.date.split('T')[0];
      const key = `${e.userId}-${dateStr}`;
      if (!map[key]) map[key] = [];
      map[key].push(e);
    });
    return map;
  }, [entries]);

  // Build absence lookup: userId -> list of absences
  const absenceLookup = useMemo(() => {
    const map: Record<string, AbsenceEntry[]> = {};
    absences.forEach((a) => {
      if (!map[a.userId]) map[a.userId] = [];
      map[a.userId].push(a);
    });
    return map;
  }, [absences]);

  // Check if a user is absent on a specific date
  function isAbsent(userId: string, date: Date): AbsenceEntry | null {
    const userAbsences = absenceLookup[userId] || [];
    const dateStr = formatDate(date);
    for (const a of userAbsences) {
      const start = a.startDate.split('T')[0];
      const end = a.endDate.split('T')[0];
      if (dateStr >= start && dateStr <= end) return a;
    }
    return null;
  }

  // ─── Mutations ────────────────────────────

  const createEntry = useMutation({
    mutationFn: (data: { projectId: string; userId: string; date: string; type: string; note?: string }) =>
      api.post('/schedule/entries', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule-entries'] });
      setAssignModal(null);
      setAssignProject('');
      setAssignType('WORK');
      setAssignNote('');
    },
  });

  const deleteEntry = useMutation({
    mutationFn: (id: string) => api.delete(`/schedule/entries/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule-entries'] });
    },
  });

  const createAbsence = useMutation({
    mutationFn: (data: typeof absenceForm) => api.post('/schedule/absences', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule-absences'] });
      setAbsenceModal(false);
      setAbsenceForm({ userId: '', type: 'VACATION', startDate: '', endDate: '', note: '' });
    },
  });

  const approveAbsence = useMutation({
    mutationFn: (id: string) => api.put(`/schedule/absences/${id}/approve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule-absences'] });
    },
  });

  const deleteAbsence = useMutation({
    mutationFn: (id: string) => api.delete(`/schedule/absences/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule-absences'] });
    },
  });

  // ─── Navigation ───────────────────────────

  const prevWeek = () => setWeekStart(addDays(weekStart, -7));
  const nextWeek = () => setWeekStart(addDays(weekStart, 7));
  const goToday = () => setWeekStart(getMonday(new Date()));
  const isToday = (d: Date) => formatDate(d) === formatDate(new Date());

  // Stats
  const totalAssignments = entries.length;
  const activeWorkers = new Set(entries.map((e) => e.userId)).size;
  const activeProjects = new Set(entries.map((e) => e.projectId)).size;
  const pendingAbsences = absences.filter((a) => !a.approved).length;

  return (
    <div>
      {/* Header */}
      <div className="sp-page-header">
        <div>
          <h1 className="sp-page-title">Plantafel</h1>
          <p className="sp-page-subtitle">
            KW {getKW(weekStart)} &middot; {formatDateRangeDE(weekStart, weekEnd)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDays(showDays === 5 ? 7 : 5)}
            className="sp-btn-secondary !py-1.5 !px-3 !text-xs"
          >
            {showDays === 5 ? 'Mo–So' : 'Mo–Fr'}
          </button>
          <button onClick={() => setAbsenceModal(true)} className="sp-btn-secondary">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
            Abwesenheit melden
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="sp-stat-card">
          <span className="sp-stat-label">Zuweisungen</span>
          <span className="sp-stat-value">{totalAssignments}</span>
        </div>
        <div className="sp-stat-card">
          <span className="sp-stat-label">Aktive Mitarbeiter</span>
          <span className="sp-stat-value">{activeWorkers}</span>
        </div>
        <div className="sp-stat-card">
          <span className="sp-stat-label">Projekte diese Woche</span>
          <span className="sp-stat-value">{activeProjects}</span>
        </div>
        <div className="sp-stat-card">
          <span className="sp-stat-label">Offene Abwesenheiten</span>
          <span className="sp-stat-value text-amber-600">{pendingAbsences}</span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-4 bg-surface-100 rounded-xl p-1 w-fit">
        <button
          onClick={() => setActiveTab('board')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'board'
              ? 'bg-white text-slate-800 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <svg className="w-4 h-4 inline mr-1.5 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Wochenplan
        </button>
        <button
          onClick={() => setActiveTab('absences')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'absences'
              ? 'bg-white text-slate-800 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <svg className="w-4 h-4 inline mr-1.5 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          Abwesenheiten
          {pendingAbsences > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">
              {pendingAbsences}
            </span>
          )}
        </button>
      </div>

      {/* ═══ BOARD TAB ═══ */}
      {activeTab === 'board' && (
        <div className="animate-fade-in">
          {/* Week Navigation */}
          <div className="flex items-center gap-3 mb-4">
            <button onClick={prevWeek} className="sp-btn-icon" title="Vorherige Woche">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button onClick={goToday} className="sp-btn-secondary !py-1.5 !px-3 !text-xs">
              Heute
            </button>
            <button onClick={nextWeek} className="sp-btn-icon" title="Nächste Woche">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <span className="text-sm text-slate-500 font-medium">
              KW {getKW(weekStart)} &middot; {weekStart.getFullYear()}
            </span>
          </div>

          {/* Schedule Grid */}
          <div className="sp-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 min-w-[180px]">
                      Mitarbeiter
                    </th>
                    {days.map((d, i) => (
                      <th
                        key={i}
                        className={`px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider border-b border-slate-200 min-w-[140px] ${
                          isToday(d)
                            ? 'bg-brand-50 text-brand-700'
                            : 'bg-slate-50 text-slate-500'
                        }`}
                      >
                        <div>{dayNames[i]}</div>
                        <div className={`text-sm font-bold ${isToday(d) ? 'text-brand-600' : 'text-slate-700'}`}>
                          {d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loadingEntries ? (
                    <tr>
                      <td colSpan={showDays + 1}>
                        <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
                          <svg className="sp-spinner" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                          </svg>
                          Laden...
                        </div>
                      </td>
                    </tr>
                  ) : workers.length === 0 ? (
                    <tr>
                      <td colSpan={showDays + 1}>
                        <div className="sp-empty">
                          <span className="text-sm font-medium">Keine Mitarbeiter vorhanden</span>
                          <span className="text-xs text-slate-400">Legen Sie Mitarbeiter in den Einstellungen an</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    workers.map((worker) => (
                      <tr key={worker.id} className="group hover:bg-slate-50/50 transition-colors">
                        {/* Worker Name */}
                        <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50/50 px-4 py-3 border-b border-slate-100 transition-colors">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                              {worker.name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .toUpperCase()
                                .slice(0, 2)}
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-slate-800">{worker.name}</div>
                              <div className="text-[10px] text-slate-400">
                                {worker.role === 'WORKER' ? 'Bauarbeiter' : worker.role === 'ADMIN' ? 'Admin' : 'Büro'}
                              </div>
                            </div>
                          </div>
                        </td>
                        {/* Day Cells */}
                        {days.map((d, di) => {
                          const dateStr = formatDate(d);
                          const key = `${worker.id}-${dateStr}`;
                          const cellEntries = entryLookup[key] || [];
                          const absence = isAbsent(worker.id, d);
                          const isTodayCell = isToday(d);

                          return (
                            <td
                              key={di}
                              className={`px-2 py-2 border-b border-slate-100 align-top ${
                                isTodayCell ? 'bg-brand-50/30' : ''
                              } ${absence ? 'bg-slate-50' : ''}`}
                            >
                              {/* Absence overlay */}
                              {absence && (
                                <div className={`text-[10px] font-medium px-2 py-1 rounded-md border mb-1 ${absenceTypeColors[absence.type]}`}>
                                  {absenceTypeLabels[absence.type]}
                                  {!absence.approved && (
                                    <span className="ml-1 text-[9px] opacity-70">(ausstehend)</span>
                                  )}
                                </div>
                              )}

                              {/* Entries */}
                              {cellEntries.map((entry) => (
                                <div
                                  key={entry.id}
                                  className={`group/entry relative text-[11px] font-medium px-2 py-1.5 rounded-md border mb-1 cursor-default ${
                                    projectColorMap[entry.projectId] || 'bg-slate-100 text-slate-700 border-slate-300'
                                  }`}
                                >
                                  <div className="truncate pr-4">
                                    {entry.project?.title || 'Projekt'}
                                  </div>
                                  {entry.type !== 'WORK' && (
                                    <div className="text-[9px] opacity-70">{scheduleTypeLabels[entry.type]}</div>
                                  )}
                                  {entry.note && (
                                    <div className="text-[9px] opacity-60 truncate">{entry.note}</div>
                                  )}
                                  {/* Delete button */}
                                  <button
                                    onClick={() => {
                                      if (confirm('Zuweisung entfernen?')) deleteEntry.mutate(entry.id);
                                    }}
                                    className="absolute top-1 right-1 w-4 h-4 rounded-full bg-white/80 text-slate-400 hover:text-red-500 hover:bg-red-50 flex items-center justify-center opacity-0 group-hover/entry:opacity-100 transition-opacity"
                                  >
                                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}

                              {/* Add button */}
                              {!absence && (
                                <button
                                  onClick={() => setAssignModal({ userId: worker.id, date: dateStr })}
                                  className="w-full flex items-center justify-center gap-1 py-1 rounded-md border border-dashed border-slate-200 text-slate-300 hover:border-brand-300 hover:text-brand-400 hover:bg-brand-50/50 transition-all text-[10px]"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                </button>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Project Legend */}
          {projects.length > 0 && entries.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs text-slate-400 font-medium mr-1 self-center">Projekte:</span>
              {projects
                .filter((p) => entries.some((e) => e.projectId === p.id))
                .map((p) => (
                  <span
                    key={p.id}
                    className={`sp-badge border text-[10px] ${projectColorMap[p.id] || 'bg-slate-100 text-slate-700 border-slate-300'}`}
                  >
                    {p.title}
                  </span>
                ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ ABSENCES TAB ═══ */}
      {activeTab === 'absences' && (
        <div className="animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800">Abwesenheiten verwalten</h2>
            <button onClick={() => setAbsenceModal(true)} className="sp-btn-primary">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Abwesenheit melden
            </button>
          </div>

          <div className="sp-card overflow-hidden">
            <table className="sp-table">
              <thead>
                <tr>
                  <th>Mitarbeiter</th>
                  <th>Art</th>
                  <th>Von</th>
                  <th>Bis</th>
                  <th>Tage</th>
                  <th>Notiz</th>
                  <th>Status</th>
                  <th className="w-28 text-right">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {absences.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="sp-empty">
                        <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center mb-3">
                          <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-emerald-700">Keine Abwesenheiten</span>
                        <span className="text-xs text-slate-400">Alle Mitarbeiter sind verfügbar</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  absences.map((a) => {
                    const start = new Date(a.startDate);
                    const end = new Date(a.endDate);
                    const diffDays = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
                    return (
                      <tr key={a.id}>
                        <td className="font-medium text-slate-800">{a.user?.name || '-'}</td>
                        <td>
                          <span className={`sp-badge border text-[10px] ${absenceTypeColors[a.type]}`}>
                            {absenceTypeLabels[a.type]}
                          </span>
                        </td>
                        <td className="text-xs text-slate-500">
                          {start.toLocaleDateString('de-DE')}
                        </td>
                        <td className="text-xs text-slate-500">
                          {end.toLocaleDateString('de-DE')}
                        </td>
                        <td className="text-xs font-semibold text-slate-700">{diffDays}</td>
                        <td className="text-xs text-slate-500">{a.note || '-'}</td>
                        <td>
                          {a.approved ? (
                            <span className="sp-badge border bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                              Genehmigt
                            </span>
                          ) : (
                            <span className="sp-badge border bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                              Ausstehend
                            </span>
                          )}
                        </td>
                        <td className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {!a.approved && (
                              <button
                                onClick={() => approveAbsence.mutate(a.id)}
                                className="sp-btn-icon hover:!text-emerald-600 hover:!bg-emerald-50"
                                title="Genehmigen"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                            )}
                            <button
                              onClick={() => {
                                if (confirm('Abwesenheit löschen?')) deleteAbsence.mutate(a.id);
                              }}
                              className="sp-btn-icon hover:!text-error-500 hover:!bg-error-50"
                              title="Löschen"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">Abwesenheitsarten</h4>
            <div className="grid grid-cols-3 gap-4 text-xs text-blue-700">
              <div>
                <span className="font-semibold">Urlaub</span>
                <p className="text-blue-600 mt-0.5">Geplanter Urlaub, muss vom Admin genehmigt werden.</p>
              </div>
              <div>
                <span className="font-semibold">Krank</span>
                <p className="text-blue-600 mt-0.5">Krankheitsbedingte Abwesenheit.</p>
              </div>
              <div>
                <span className="font-semibold">Sonstiges</span>
                <p className="text-blue-600 mt-0.5">Fortbildung, Behördengang, etc.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ASSIGN MODAL ═══ */}
      {assignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 animate-scale-in">
            <h3 className="text-lg font-bold text-slate-800 mb-1">Mitarbeiter zuweisen</h3>
            <p className="text-xs text-slate-400 mb-5">
              {workers.find((w) => w.id === assignModal.userId)?.name} &middot;{' '}
              {new Date(assignModal.date + 'T00:00:00').toLocaleDateString('de-DE', {
                weekday: 'long',
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </p>

            <div className="space-y-4">
              <div>
                <label className="sp-label">Projekt</label>
                <select
                  value={assignProject}
                  onChange={(e) => setAssignProject(e.target.value)}
                  className="sp-input"
                >
                  <option value="">Projekt auswählen...</option>
                  {projects
                    .filter((p) => ['ORDER', 'IN_PROGRESS'].includes(p.status))
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title} {p.customer ? `(${p.customer.name})` : ''}
                      </option>
                    ))}
                  {projects.filter((p) => ['ORDER', 'IN_PROGRESS'].includes(p.status)).length === 0 &&
                    projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="sp-label">Typ</label>
                <select
                  value={assignType}
                  onChange={(e) => setAssignType(e.target.value as 'WORK' | 'TRAVEL')}
                  className="sp-input"
                >
                  <option value="WORK">Arbeit</option>
                  <option value="TRAVEL">Fahrt</option>
                </select>
              </div>

              <div>
                <label className="sp-label">Notiz (optional)</label>
                <input
                  value={assignNote}
                  onChange={(e) => setAssignNote(e.target.value)}
                  className="sp-input"
                  placeholder="z.B. Vormittag, Nachmittag..."
                />
              </div>
            </div>

            <div className="sp-form-actions mt-6">
              <button
                onClick={() => {
                  if (!assignProject) return;
                  createEntry.mutate({
                    projectId: assignProject,
                    userId: assignModal.userId,
                    date: assignModal.date,
                    type: assignType,
                    note: assignNote || undefined,
                  });
                }}
                disabled={!assignProject || createEntry.isPending}
                className="sp-btn-primary"
              >
                {createEntry.isPending ? 'Speichern...' : 'Zuweisen'}
              </button>
              <button
                onClick={() => {
                  setAssignModal(null);
                  setAssignProject('');
                  setAssignType('WORK');
                  setAssignNote('');
                }}
                className="sp-btn-secondary"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ABSENCE MODAL ═══ */}
      {absenceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 animate-scale-in">
            <h3 className="text-lg font-bold text-slate-800 mb-4">Abwesenheit melden</h3>

            <div className="space-y-4">
              <div>
                <label className="sp-label">Mitarbeiter</label>
                <select
                  value={absenceForm.userId}
                  onChange={(e) => setAbsenceForm({ ...absenceForm, userId: e.target.value })}
                  className="sp-input"
                >
                  <option value="">Mitarbeiter auswählen...</option>
                  {workers.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="sp-label">Art</label>
                <select
                  value={absenceForm.type}
                  onChange={(e) =>
                    setAbsenceForm({ ...absenceForm, type: e.target.value as 'VACATION' | 'SICK' | 'OTHER' })
                  }
                  className="sp-input"
                >
                  <option value="VACATION">Urlaub</option>
                  <option value="SICK">Krank</option>
                  <option value="OTHER">Sonstiges</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="sp-label">Von</label>
                  <input
                    type="date"
                    value={absenceForm.startDate}
                    onChange={(e) => setAbsenceForm({ ...absenceForm, startDate: e.target.value })}
                    className="sp-input"
                  />
                </div>
                <div>
                  <label className="sp-label">Bis</label>
                  <input
                    type="date"
                    value={absenceForm.endDate}
                    onChange={(e) => setAbsenceForm({ ...absenceForm, endDate: e.target.value })}
                    className="sp-input"
                  />
                </div>
              </div>

              <div>
                <label className="sp-label">Notiz (optional)</label>
                <input
                  value={absenceForm.note}
                  onChange={(e) => setAbsenceForm({ ...absenceForm, note: e.target.value })}
                  className="sp-input"
                  placeholder="z.B. Arztbesuch, Fortbildung..."
                />
              </div>
            </div>

            <div className="sp-form-actions mt-6">
              <button
                onClick={() => {
                  if (!absenceForm.userId || !absenceForm.startDate || !absenceForm.endDate) return;
                  createAbsence.mutate(absenceForm);
                }}
                disabled={!absenceForm.userId || !absenceForm.startDate || !absenceForm.endDate || createAbsence.isPending}
                className="sp-btn-primary"
              >
                {createAbsence.isPending ? 'Speichern...' : 'Melden'}
              </button>
              <button
                onClick={() => {
                  setAbsenceModal(false);
                  setAbsenceForm({ userId: '', type: 'VACATION', startDate: '', endDate: '', note: '' });
                }}
                className="sp-btn-secondary"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
